import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Smartphone, CreditCard, Banknote, Globe, Star } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { StructuredPaymentTerms, PaymentMethodType } from '../../types';

interface PaymentTermsDisplayProps {
  terms?: StructuredPaymentTerms;
}

const paymentMethodIcons: Record<PaymentMethodType, React.ReactNode> = {
  bank_transfer: <Building2 className="h-5 w-5" />,
  twint: <Smartphone className="h-5 w-5" />,
  credit_card: <CreditCard className="h-5 w-5" />,
  cash: <Banknote className="h-5 w-5" />,
  paypal: <Globe className="h-5 w-5" />,
};

const PaymentTermsDisplay: React.FC<PaymentTermsDisplayProps> = ({ terms }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  if (!terms) {
    return (
      <div className="text-muted-foreground text-center py-8">
        {t('common.noContent')}
      </div>
    );
  }

  // Sort cancellation tiers by days (descending)
  const sortedCancellationPolicy = [...(terms.cancellationPolicy || [])].sort(
    (a, b) => b.daysBeforeArrival - a.daysBeforeArrival
  );

  return (
    <div className="space-y-6">
      {/* Bankverbindung — immer sichtbar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Bankverbindung
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <span className="text-muted-foreground">Inhaber</span>
              <span className="font-medium">Yvo Tobias, Lucas Patrick &amp; Dominik Michael Richner</span>
              <span className="text-muted-foreground">Konto</span>
              <span>Privatkonto CHF – Rte du Lac 37, Môtier</span>
              <span className="text-muted-foreground">IBAN</span>
              <span className="font-mono">CH85 0076 9434 1800 7200 2</span>
              <span className="text-muted-foreground">BIC</span>
              <span className="font-mono">BLKBCH22</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Methods */}
      {terms.paymentMethods && terms.paymentMethods.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('payment.methods')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {terms.paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className={`p-4 rounded-lg border ${
                    method.isPreferred
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-muted/30'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className={method.isPreferred ? 'text-primary' : 'text-muted-foreground'}>
                      {paymentMethodIcons[method.type]}
                    </span>
                    <span className="font-medium">{method.name[language]}</span>
                    {method.isPreferred && (
                      <Badge variant="default" className="ml-auto">
                        <Star className="h-3 w-3 mr-1" />
                        {t('payment.preferred')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {method.details[language]}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancellation Policy */}
      {sortedCancellationPolicy.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Stornierungsbedingungen</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stornierungszeitpunkt</TableHead>
                  <TableHead className="text-right">Stornierungsgebühr</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCancellationPolicy.map((tier) => (
                  <TableRow key={tier.id}>
                    <TableCell>
                      {tier.daysBeforeArrival} Tage vor Anreise
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={tier.refundPercentage === 100 ? 'default' : tier.refundPercentage > 0 ? 'secondary' : 'destructive'}>
                        {tier.refundPercentage}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Payment deadline */}
      {terms.paymentDeadlineDays > 0 && (
        <Card>
          <CardContent className="pt-5">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Zahlungsfrist</span>
              <span className="font-semibold">{terms.paymentDeadlineDays} Tage</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentTermsDisplay;
